// Qué me pongo hoy.
//
// Es la función número uno de la app, y la última en llegar: el motor
// estaba escrito y probado hace rato, pero sin pantalla no servía de nada.
//
// Tres decisiones que definen cómo se siente:
//
// 1. NO hay que elegir filtros para ver algo. Se abre y ya hay outfits.
//    Una pantalla que arranca con un formulario vacío pidiendo ocasión y
//    estación es una pantalla que no se usa un martes a las 7 de la
//    mañana.
//
// 2. Se muestran POCAS combinaciones a la vez. El armario da más de diez
//    mil; mostrarlas todas no es generosidad, es paralizar. Vienen las
//    mejores primero (lo que hace más tiempo no usás) y hay un botón para
//    barajar de nuevo.
//
// 3. "Me lo puse" es un solo clic. Ese registro es lo que hace que el
//    orden por frescura signifique algo mañana. Si costara más de un
//    clic, nadie lo haría, y la app nunca aprendería nada del uso real.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import * as api from "./api";
import { cargarOpciones } from "./opciones";
import Visor from "./Visor";
import { hoyLocal, hace, aISO } from "./fechas";
import {
  alternativasPara,
  aplicarCambios,
  filtrarPrendas,
  registrarCambio,
} from "./seleccion";
import { tintesDelOutfit, DESCONOCIDO } from "./tintes";
import { fotoChica, fotoGrande } from "./foto";
/* ⚠️  `tPlural` FALTABA EN ESTA LISTA (2026-09-05).
 *
 * Se usa en el panel de alternativas —"{opciones} · primero las que
 * hace más tiempo no usás"— y sin el import eso es
 * `ReferenceError: tPlural is not defined`: el panel no se dibuja y se
 * lleva puesta la pantalla. Lo encontró `no-undef`, no una persona.
 *
 * Es la misma clase de error que la ficha en blanco del 4/9: no se ve
 * al compilar, no se ve leyendo el archivo como texto, solo se ve
 * ejecutando o preguntándole al linter si lo que el código nombra
 * existe. */
import { t, tCat, tError, tPlural, numero, useIdioma } from "./i18n";
import { IconoFichada } from "./Iconos";
import FrascoBoton from "./FrascoBoton";
import { perfumeSugerido } from "./perfumesDatos";
import { useFocoAtrapado } from "./foco";

// Qué filtro se está saltando el ancla, en palabras. El servidor manda
// la clave y no el texto: si mandara el texto, traducir la app
// obligaría a tocar el backend.
const MOTIVO_ANCLA = {
  ocasion: "la ocasión elegida",
  estacion: "la estación elegida",
  disponibilidad: "estar disponible (está en el lavadero o guardada)",
  desgaste: "el estado (está gastada para esta ocasión)",
};

/* La identidad ESTABLE de una combinación: sus prendas.
 *
 * `combo.id` no sirve para esto. Es posicional —el motor los emite como
 * "combo-0", "combo-1"…— así que la misma combinación cambia de id
 * entre dos consultas y dos combinaciones distintas comparten id. Se
 * usa para `vistas` y para `registrados`, y por eso está escrita UNA
 * vez: dos definiciones de "es la misma combinación" terminan
 * discrepando.
 *
 * ⚠️  VA FUERA DEL COMPONENTE. Adentro sería una función nueva en cada
 *     render; `traer` la tendría que declarar como dependencia de su
 *     `useCallback`, cambiaría de identidad en cada render, y el efecto
 *     que depende de `traer` volvería a pedir las sugerencias en bucle.
 *     No usa nada del estado, así que acá afuera es donde va. */
function claveDePrendas(prendas) {
  return (prendas || []).map((p) => p.id).sort().join("|");
}

export default function Outfits({ recargarToken, ancla, onLimpiarAncla, onRegistrado }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  const [opciones, setOpciones] = useState(null);

  /* EL PERFUME DE HOY (22/9). Uno para toda la pantalla, no uno por
   * tarjeta: es lo que te pusiste hoy, y no depende de cuál de las 18
   * combinaciones elijas. Arranca con el último que usaste (o el último
   * que cargaste) y se cambia desde el frasco de cualquier tarjeta.
   *
   * Si la lista no se puede traer —la base todavía sin `perfumes.sql`,
   * un corte—, no pasa nada: el frasco no aparece y registrar sigue
   * andando como siempre. Los perfumes son un agregado, no una
   * dependencia de «Me lo puse». */
  const [perfumes, setPerfumes] = useState([]);
  const [perfumeHoy, setPerfumeHoy] = useState(null);
  useEffect(() => {
    let vivo = true;
    api
      .listarPerfumes()
      .then((d) => {
        if (!vivo) return;
        setPerfumes(d.perfumes || []);
        setPerfumeHoy(perfumeSugerido(d.perfumes)?.id || null);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [recargarToken]);
  const [datos, setDatos] = useState(null);
  /* LA OCASIÓN ELEGIDA SE RECUERDA, igual que la estación.
   *
   * Casi nadie cambia de ocasión entre una apertura y la siguiente: el
   * que se viste para trabajar se viste para trabajar todos los días.
   * Sin memoria, esa persona paga el mismo scroll y el mismo clic cada
   * vez que abre la pantalla.
   *
   * Vive en localStorage y no en el perfil por la misma razón que la
   * estación: es un dato del día, no una preferencia que valga viajar
   * al servidor y volver. Y no vale una columna nueva ni una migración.
   *
   * ⚠️  UNA OCASIÓN RECORDADA PUEDE HABER DEJADO DE EXISTIR. Se borra
   *     una ocasión propia, o se cambia de lista, y lo guardado acá
   *     queda apuntando a un nombre que el servidor ya no acepta:
   *     responde 400 UNKNOWN_OCCASION. O sea que la pantalla se abriría
   *     con un error, rompiendo la regla 1 de este archivo — "no hay
   *     que elegir filtros para ver algo". Por eso se valida contra la
   *     lista vigente ANTES de usarla (ver `ocasionPorValidar`). */
  const ocasionRecordada = () => {
    try {
      return localStorage.getItem("brujel_ocasion") || null;
    } catch {
      return null; // navegación privada: se abre sin filtro, y ya
    }
  };

  const [filtros, setFiltros] = useState(() => {
    const guardada = ocasionRecordada();
    return guardada ? { ocasion: guardada } : {};
  });

  /* Mientras esto tenga un valor, la ocasión recordada TODAVÍA NO se
   * comprobó contra la lista del usuario y la consulta espera.
   *
   * Ese es el precio, y se paga solo donde hace falta: quien no tiene
   * ninguna ocasión recordada arranca con `null` acá y su pantalla no
   * espera nada — se comporta exactamente como antes de este cambio. */
  const [ocasionPorValidar, setOcasionPorValidar] = useState(ocasionRecordada);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [ampliada, setAmpliada] = useState(null);

  // Qué combinaciones ya registraste en esta sesión. Sirve para que el
  // botón cambie a "Registrado" sin recargar toda la pantalla.
  const [registrados, setRegistrados] = useState({});

  // Cambios que hiciste sobre una sugerencia, antes de confirmarla:
  //   { [idCombo]: { [idPrendaOriginal]: prendaNueva } }
  //
  // Se guarda el mapa original→reemplazo y no la lista final, para
  // poder marcar el outfit como editado y ofrecer deshacer. Con solo la
  // lista final, un outfit tocado sería indistinguible de uno que salió
  // así del motor.
  const [cambios, setCambios] = useState({});

  // El armario completo, para poder ofrecer reemplazos. Se pide UNA vez
  // y recién cuando hace falta: quien nunca cambia una prenda no paga
  // el request.
  const [armario, setArmario] = useState(null);
  const [cambiando, setCambiando] = useState(null); // { combo, prenda }
  // Ver src/foco.js (BA-14).
  const cajaCambiar = useFocoAtrapado(!!cambiando);

  /* Escape cierra, igual que en los otros cinco modales.
   *
   * Faltaba, y lo encontró `test_accesibilidad.js` §2d: este modal se
   * cerraba SOLO tocando afuera. Con el mouse es invisible; con teclado
   * era una habitación sin puerta. La regla que lo destapó dice que un
   * fondo de modal se perdona por no ser un botón solo si Escape hace
   * lo mismo — y acá no lo hacía. */
  useEffect(() => {
    if (!cambiando) return undefined;
    const alTeclear = (e) => {
      if (e.key === "Escape") setCambiando(null);
    };
    window.addEventListener("keydown", alTeclear, true);
    return () => window.removeEventListener("keydown", alTeclear, true);
  }, [cambiando]);

  // Una sola función para traerlo, porque ahora lo necesitan dos cosas:
  // cambiar una prenda de una sugerencia, y buscar una para anclar.
  // Duplicar la carga sería duplicar el request.
  /* ⚠️  EL `if (armario) return` SIN NADA QUE LO INVALIDE ERA UN CACHÉ
   *     QUE NO SE VENCÍA NUNCA (BA-13, arreglado el 2026-09-03).
   *
   * Se pide una vez por montaje, que es lo correcto: quien nunca cambia
   * una prenda no paga el request. Pero nada lo borraba, así que editar
   * una prenda desde el visor dejaba la lista de reemplazos y la de
   * anclar con el dato viejo — con la estación anterior, el nombre
   * anterior y la disponibilidad anterior.
   *
   * El síntoma es cruel: las SUGERENCIAS sí se recargaban
   * (`onPrendaEditada`), así que la pantalla se veía actualizada y solo
   * el desplegable de "Cambiar" mentía. Un dato viejo al lado de uno
   * nuevo es peor que dos viejos: no hay forma de sospecharlo.
   *
   * `olvidarArmario()` lo tira; el próximo que lo necesite lo vuelve a
   * pedir. */
  async function cargarArmario() {
    if (armario) return;
    try {
      const d = await api.listarPrendas({ orden: "olvidadas" });
      setArmario(d.prendas);
    } catch (e) {
      setError(tError(e));
    }
  }

  function olvidarArmario() {
    setArmario(null);
  }

  async function abrirCambio(combo, prenda) {
    setCambiando({ combo, prenda });
    await cargarArmario();
  }

  /* ------------------------------------------------------------------
     Anclar una prenda
  ------------------------------------------------------------------ */

  // "Quiero ponerme ESTO, ¿con qué va?"
  //
  // El motor ya sabía hacerlo —`anclaId` existe desde el principio— pero
  // solo se llegaba desde "Mi armario": había que ir, encontrar la
  // prenda, y tocar "ver combinaciones". Tres pantallas para una
  // pregunta que se hace parado frente al placard.
  //
  // El ancla puede llegar de dos lados: del armario (por prop) o de esta
  // búsqueda. Se guarda en UN estado y no en dos, porque dos fuentes
  // para el mismo dato es cómo se termina mostrando una prenda y
  // filtrando por otra.
  const [anclaId, setAnclaId] = useState(ancla || null);
  const [textoAncla, setTextoAncla] = useState("");

  useEffect(() => {
    if (ancla) setAnclaId(ancla);
  }, [ancla]);

  function elegirAncla(id) {
    setAnclaId(id);
    setTextoAncla("");
    // El ancla que vino del armario se apaga: a partir de acá manda la
    // elegida acá.
    onLimpiarAncla?.();
  }

  function quitarAncla() {
    setAnclaId(null);
    onLimpiarAncla?.();
  }

  // Se muestran pocas: una lista de 164 prendas debajo de un buscador
  // no es un resultado, es el armario otra vez.
  const RESULTADOS_ANCLA = 8;

  const candidatasAncla = useMemo(() => {
    if (!armario || !textoAncla.trim()) return [];
    return filtrarPrendas(armario, { texto: textoAncla }).slice(
      0,
      RESULTADOS_ANCLA
    );
  }, [armario, textoAncla]);

  function cambiarPrenda(combo, mostrada, nueva) {
    // `registrarCambio` traduce la prenda que estás VIENDO al id de la
    // que estaba originalmente en ese lugar. Sin eso, cambiar dos veces
    // la misma prenda guardaba el cambio bajo un id que no existe en la
    // combinación original, y el segundo cambio no hacía nada.
    setCambios((c) => {
      const paraEsteCombo = registrarCambio(c[combo.id], mostrada, nueva);
      const siguiente = { ...c };
      // Sin cambios = sin entrada. Así la tarjeta no queda marcada como
      // "Editado" después de volver a la prenda original.
      if (Object.keys(paraEsteCombo).length === 0) delete siguiente[combo.id];
      else siguiente[combo.id] = paraEsteCombo;
      return siguiente;
    });
    setCambiando(null);
  }

  /* Sacar una prenda de la sugerencia.
   *
   * El caso que resuelve: la combinación te sirve pero hoy no querés
   * abrigo, o el accesorio no va. Hasta ahora la única salida era
   * reemplazarla por otra cosa —que no es lo mismo— o descartar el
   * outfit entero.
   *
   * ⚠️  NO SE PUEDE SACAR LA ÚLTIMA. Un outfit de cero prendas no es un
   *     outfit: el servidor lo rechaza con GARMENT_LIST_MISSING y la
   *     pantalla mostraría un error por algo que nunca debió poder
   *     tocarse. Se apaga el botón antes, que es más honesto que
   *     dejarlo tocar y explicar después.
   *
   * Sí se puede quedar con UNA sola, y es a propósito: registrar "hoy
   * me puse esta camisa" es un dato válido aunque no cubra el cuerpo
   * entero. La regla de cubrir torso, piernas y pies es del motor que
   * SUGIERE, no del registro de lo que realmente te pusiste. */
  function quitarPrenda(combo, mostrada) {
    setCambios((c) => {
      const paraEsteCombo = registrarCambio(c[combo.id], mostrada, null);
      return { ...c, [combo.id]: paraEsteCombo };
    });
  }

  function deshacerCambios(comboId) {
    setCambios((c) => {
      const n = { ...c };
      delete n[comboId];
      return n;
    });
  }

  // Las prendas de un combo con los cambios aplicados. Todo lo que
  // registra, veta o muestra la pantalla usa ESTO y no `combo.prendas`
  // — si alguna parte usara el original, registrarías un outfit
  // distinto del que estás viendo.
  const prendasDe = (combo) => aplicarCambios(combo.prendas, cambios[combo.id]);


  // La semilla del azar. Cambiarla es todo lo que hace "Otras
  // opciones": el servidor devuelve un conjunto distinto de
  // combinaciones sin que se toque ningún filtro.
  const [semilla, setSemilla] = useState(1);

  // Las firmas de todas las combinaciones que ya se mostraron con los
  // filtros actuales. Se vacía cuando cambia cualquier filtro: con otro
  // filtro estás mirando otro conjunto y arrastrar la cuenta anterior
  // sería mentir.
  const [vistas, setVistas] = useState(() => new Set());

  // LA ESTACIÓN REEMPLAZA AL TERMÓMETRO (2026-08-20)
  //
  // Antes había dos controles que preguntaban casi lo mismo: el
  // termómetro (templado / fresco / frío) y un select de estación. Ahora
  // es uno solo, porque la estación ya dice las dos cosas — qué ropa
  // corresponde y cuánto abrigo hace falta.
  //
  // Arranca en la estación REAL del país del usuario, que llega
  // calculada del servidor. Sin país cargado viene null y no se
  // preselecciona nada: el motor no filtra y se ve el armario entero,
  // igual que antes de que este campo existiera.
  //
  // Lo elegido vive en localStorage y no en el perfil: es un dato del
  // día, no una preferencia. Y se recuerda porque si ayer era invierno
  // hoy también.
  const { estacionActual } = useAuth();

  /* ⚠️  TRES ESTADOS, NO DOS — Y ES LA REGLA DE LA CASA (BA-12,
   *     arreglado el 2026-09-03).
   *
   * Acá había DOS estados donde el problema tiene TRES:
   *
   *     undefined  todavía no decidiste     → adoptar la de hoy
   *     null       la apagaste a propósito  → respetarlo
   *     "Verano"   elegiste esa             → respetarlo
   *
   * Con solo `null`, "apagada" y "nunca elegí" eran el mismo valor:
   * apagabas el filtro para ver el armario entero, cambiabas de
   * pestaña, volvías, y la estación de hoy estaba puesta de nuevo. El
   * usuario tenía que apagarla en cada visita.
   *
   * Es exactamente la distinción que este proyecto ya escribió para el
   * motor —ausente = "no sé", null = "miré y no aplica"— aplicada a la
   * interfaz, que es donde faltaba.
   *
   * En localStorage el "apagada" necesita un valor guardable, porque
   * borrar la clave es indistinguible de no haberla escrito nunca. */
  const ESTACION_APAGADA = "ninguna";

  const [estacion, setEstacion] = useState(() => {
    try {
      const guardada = localStorage.getItem("brujel_estacion");
      if (guardada === ESTACION_APAGADA) return null; // apagada a propósito
      if (guardada) return guardada; // elegida a mano
      return undefined; // nunca decidió
    } catch {
      return undefined;
    }
  });

  // La primera vez que el servidor dice qué estación es, se adopta —
  // salvo que el usuario ya haya decidido, y "apagada" ES una decisión.
  useEffect(() => {
    if (!estacionActual) return;
    setEstacion((actual) => (actual === undefined ? estacionActual : actual));
  }, [estacionActual]);

  function elegirEstacion(v) {
    // Tocar la que ya está elegida la apaga. Es la salida para ver el
    // armario entero sin filtro, sin tener que agregar un botón "todas".
    const nueva = v === estacion ? null : v;
    setEstacion(nueva);
    try {
      // Se escribe SIEMPRE, incluso al apagar: el centinela es lo que
      // distingue "la apagué" de "nunca entré".
      localStorage.setItem("brujel_estacion", nueva || ESTACION_APAGADA);
    } catch {
      // Navegación privada o storage lleno. Pierde la memoria entre
      // sesiones y nada más.
    }
  }

  // Las cuatro, empezando por la de hoy. Que enero arranque en
  // "Verano" para alguien en Buenos Aires y en "Invierno" para alguien
  // en Madrid es todo el punto del campo país.
  const estacionesOrdenadas = useMemo(() => {
    const todas = opciones?.estaciones || [];
    if (!estacionActual || !todas.includes(estacionActual)) return todas;
    const i = todas.indexOf(estacionActual);
    return [...todas.slice(i), ...todas.slice(0, i)];
  }, [opciones, estacionActual]);

  useEffect(() => {
    cargarOpciones().then(setOpciones);
  }, []);

  /* ⚠️  MISMA GUARDIA QUE EN ARMARIO Y COMPRAR (ME-6, 2026-09-03).
   *
   * Acá es la más cara de las tres: cada respuesta trae 18
   * combinaciones y ADEMÁS acumula firmas en `vistas`. Una respuesta
   * vieja que llega tarde no solo dibuja outfits que no corresponden al
   * filtro puesto: le suma sus firmas al contador de "ya viste", y ese
   * número no se puede desinflar.
   *
   * Con `semilla` en las dependencias, tocar "Otras opciones" dos veces
   * rápido es exactamente el caso. Ver el comentario largo en
   * Armario.js. */
  const pedidoVigente = useRef(0);

  const traer = useCallback(async () => {
    const mio = ++pedidoVigente.current;
    setCargando(true);
    try {
      const d = await api.sugerirOutfits({
        ...filtros,
        ancla: anclaId || undefined,
        semilla,
        estacion,
      });
      // Todo lo que sigue ESCRIBE estado. Si este pedido ya no es el
      // vigente, se descarta entero: dibujar la mitad sería peor.
      if (mio !== pedidoVigente.current) return;
      setError("");
      setDatos(d);

      /* CUÁNTAS YA VISTE, acumulado entre pasadas de "Otras opciones".
       *
       * Se cuentan FIRMAS —los ids de prenda ordenados— y no los ids de
       * combinación. Los ids son posicionales ("combo-0", "combo-1") y
       * se reasignan en cada consulta, así que contarlos diría "viste
       * 36" cuando en realidad viste las mismas 18 dos veces.
       *
       * Con firmas, si "Otras opciones" te devuelve una combinación que
       * ya habías visto, no suma. El número dice lo que promete.
       */
      setVistas((antes) => {
        const set = new Set(antes);
        for (const c of d.combinaciones || []) set.add(claveDePrendas(c.prendas));
        return set;
      });

      /* ⚠️  `registrados` YA NO SE BORRA, Y ESO ES LO QUE HACE SEGURA LA
       *     RECARGA INMEDIATA (2026-09-05).
       *
       * Antes se vaciaba en cada consulta porque estaba indexado por
       * `combo.id`, que es POSICIONAL —"combo-0", "combo-1"…— y se
       * reasigna en cada respuesta. Con esa clave, conservarlo habría
       * puesto el "✓ Registrado" sobre un outfit distinto.
       *
       * Ahora la clave son las prendas, la misma que usa `vistas`. Es
       * estable entre consultas, así que la marca sigue a la
       * combinación de verdad: si vuelve a salir, la muestra; si no
       * vuelve a salir, no la muestra en ninguna otra.
       *
       * Se poda a lo que está en pantalla para que el mapa no crezca
       * sin techo en una sesión larga de "Otras opciones". */
      setRegistrados((antes) => {
        const vivas = new Set((d.combinaciones || []).map((c) => claveDePrendas(c.prendas)));
        const podado = {};
        for (const [k, v] of Object.entries(antes)) if (vivas.has(k)) podado[k] = v;
        return podado;
      });
      // Los ids de combo se reasignan en cada consulta ("combo-0",
      // "combo-1"...), así que un cambio guardado se aplicaría a un
      // outfit distinto. Se limpian junto con los datos.
      setCambios({});
    } catch (e) {
      if (mio !== pedidoVigente.current) return;
      setError(tError(e));
    } finally {
      if (mio === pedidoVigente.current) setCargando(false);
    }
  }, [filtros, anclaId, semilla, estacion]);

  /* La ocasión recordada se comprueba contra la lista VIGENTE en cuanto
   * llegan las opciones. Si ya no existe se olvida en silencio: el
   * usuario borró esa ocasión, no cometió un error que haya que
   * contarle. */
  useEffect(() => {
    if (!opciones || !ocasionPorValidar) return;
    if (!(opciones.categorias || []).includes(ocasionPorValidar)) {
      setFiltros((antes) => {
        const nuevos = { ...antes };
        delete nuevos.ocasion;
        return nuevos;
      });
      try {
        localStorage.removeItem("brujel_ocasion");
      } catch {
        /* nada que limpiar si no se puede escribir */
      }
    }
    setOcasionPorValidar(null);
  }, [opciones, ocasionPorValidar]);

  /* ⚠️  ESTA PANTALLA SÍ SE RECARGA AL REGISTRAR UN USO (2026-09-05).
   *
   * Antes NO lo hacía, y era una decisión de producto razonable: si la
   * lista se reordena en el mismo instante en que tocás el botón, la
   * tarjeta que acabás de marcar se va de lugar. El comentario que
   * estaba acá ya nombraba el precio —"una lista vieja hasta el próximo
   * cambio de filtro"— y ese precio resultó ser más caro de lo que
   * parecía escrito.
   *
   * REPORTADO EL 2026-09-05: "me aparece en la primera pantalla una
   * polo que me puse hoy". Y era cierto. Antes de marcarla, esa polo
   * estaba en la banda 0 (sin estrenar) y sus combinaciones salían
   * arriba de todo. Marcarla la manda a la banda 3 —la peor— pero solo
   * en la próxima consulta, que no llegaba nunca. El usuario seguía
   * viendo, arriba de todo, la ropa que acababa de decir que se puso.
   *
   * La razón por la que no se recargaba ya no existe: `registrados`
   * está indexado por las PRENDAS y no por `combo.id`, así que el
   * "✓ Registrado" sigue a la combinación entre consultas en vez de
   * quedar flotando en otra. Ver `claveDePrendas`. */
  useEffect(() => {
    /* ⚠️  `traer()` VA PEGADO A ESTE GUARD, Y NO ES CAPRICHO.
     *
     * `test_api_cliente.js` lo exige, porque lo que cuida es que la
     * consulta ESPERE a la validación: si algo se mete en el medio, un
     * futuro `return` condicional podría dejar salir el pedido con un
     * filtro que el servidor rechaza. La regla es correcta y el código
     * se acomoda a ella, no al revés.
     *
     * Con una ocasión sin validar, pedir sería pedir con un filtro que
     * el servidor puede rechazar. Se espera —solo en ese caso— a que el
     * efecto de arriba la confirme o la descarte. */
    if (ocasionPorValidar) return;
    traer();
  }, [traer, recargarToken, ocasionPorValidar]);

  // El reseteo va en su PROPIO efecto y sin `semilla` en las
  // dependencias. Si viviera adentro de `traer`, cada "Otras opciones"
  // borraría la cuenta — que es exactamente lo contrario de lo que el
  // contador tiene que decir.
  useEffect(() => {
    setVistas(new Set());
  }, [filtros, anclaId, estacion]);

  function poner(clave, valor) {
    setFiltros((antes) => {
      const nuevos = { ...antes };
      if (valor) nuevos[clave] = valor;
      else delete nuevos[clave];
      return nuevos;
    });

    /* Solo la ocasión se recuerda entre sesiones. Los demás filtros son
     * de una búsqueda puntual —"a ver qué tengo en azul"— y devolverlos
     * al abrir sería esconder medio armario sin que nadie lo pidiera. */
    if (clave === "ocasion") {
      try {
        if (valor) localStorage.setItem("brujel_ocasion", valor);
        else localStorage.removeItem("brujel_ocasion");
      } catch {
        // Navegación privada o storage lleno: se pierde la memoria
        // entre sesiones y nada más.
      }
    }
  }

  async function registrar(combo, fecha) {
    // Optimista: el botón cambia enseguida. Si el servidor falla se
    // revierte y se muestra el error. Registrar un uso no es una
    // operación que valga hacer esperar al usuario.
    // Por prendas y no por `combo.id`: al recargar, los ids se
    // reasignan y la marca caería sobre otra combinación.
    const clave = claveDePrendas(prendasDe(combo));
    setRegistrados((a) => ({ ...a, [clave]: "yendo" }));
    try {
      await api.registrarOutfit({
        prenda_ids: prendasDe(combo).map((p) => p.id),
        ocasion: filtros.ocasion || null,
        usada_en: fecha ? aISO(fecha) : null,
        perfume_id: perfumeHoy || null,
      });
      setRegistrados((a) => ({ ...a, [clave]: "ok" }));
      /* ⚠️  ESTA LÍNEA NO EXISTÍA, Y ERA UN BUG REPORTADO POR EL DUEÑO
       *     (2026-08-27): *"no aparece en que me pongo como que vestí
       *     una prenda"*.
       *
       * `App.js` monta esta pantalla SIN `alGuardar` —esa prop se la
       * lleva `Armador`— así que registrar un uso desde acá no le
       * avisaba a nadie. El outfit SE GUARDABA (por eso aparecía en el
       * historial), pero:
       *
       *   · no salía el aviso "Registrado: N prendas";
       *   · el armario y el historial quedaban con el dato viejo hasta
       *     que la pantalla se volviera a montar.
       *
       * La única señal era el "✓ Registrado" del propio botón, y eso se
       * lee como "lo anoté acá" y no como "lo anotó la app".
       *
       * Subir `recargarToken` recarga TAMBIÉN esta pantalla, a
       * propósito desde el 2026-09-05: la prenda que acabás de usar
       * tiene que dejar de salir primera. */
      onRegistrado?.(prendasDe(combo).length);
    } catch (e) {
      setRegistrados((a) => {
        const c = { ...a };
        delete c[clave];
        return c;
      });
      setError(tError(e));
    }
  }

  // Vetar saca la combinación de la lista al instante. Es la respuesta
  // correcta: dijiste que no va, no tiene por qué seguir ahí ocupando
  // lugar. Las demás que compartan el par vetado desaparecen en la
  // próxima consulta.
  async function vetar(combo, a, b, motivo) {
    try {
      /* ⚠️  `combo.prendas` Y NO `prendasDe(combo)`, al revés que
       * `registrar`. No es un descuido: son dos preguntas distintas.
       *
       * `registrar` graba LO QUE TE PUSISTE, así que manda lo que hay
       * en pantalla, con los cambios aplicados. Si cambiaste una
       * prenda, el hash no coincide con ninguna impresión y queda
       * `elegida: -1` — "registró algo que no le propuse", que es
       * información y está documentado.
       *
       * El veto contesta otra cosa: CUÁL DE LAS QUE TE MOSTRÉ
       * rechazaste. Eso es una etiqueta sobre una combinación mostrada,
       * y la combinación mostrada es la que devolvió el motor. Mandar
       * la versión con cambios daría -1 justo cuando más se sabe.
       *
       * Va como cuarto argumento y es opcional: solo alimenta la
       * telemetría. El veto en sí es el par (a, b) y no depende de esto.
       */
      const mostradas = (combo.prendas || []).map((p) => p.id);
      await api.vetarPar(a, b, motivo, mostradas);
      setDatos((d) => ({
        ...d,
        combinaciones: d.combinaciones.filter((c) => c.id !== combo.id),
        vetosActivos: (d.vetosActivos || 0) + 1,
      }));
    } catch (e) {
      setError(tError(e));
    }
  }

  if (!opciones) return <p className="tenue">{t("comun.cargando", "Cargando...")}</p>;

  // `sin_reglas` no es un filtro: es lo contrario, muestra MÁS. Tiene su
  // propio botón en la barra de reglas.
  const hayFiltros = Object.keys(filtros).some((k) => k !== "sin_reglas");

  return (
    <section>
      {/* Cuando venís desde una prenda del armario, la prenda manda:
          se muestra arriba de todo y todas las combinaciones la
          incluyen. Sin este cartel no habría forma de saber por qué
          estas 24 y no otras. */}
      {anclaId && datos?.ancla && (
        <div className="ancla">
          {fotoChica(datos.ancla) && (
            <img loading="lazy" decoding="async" src={fotoChica(datos.ancla)} alt="" className="ancla-foto" />
          )}
          <div>
            <p className="texto-mini" style={{ margin: 0 }}>
              {t("outfits.combinacionesCon", "Combinaciones con")} <strong>{datos.ancla.nombre}</strong>
            </p>
            {/* El ancla entra aunque no cumpla los filtros —la elegiste
                vos— pero antes eso pasaba en silencio: anclabas una
                remera de gym con la ocasión en "Trabajo" y veías 24
                combinaciones sin entender que ninguna respeta el filtro
                que tenías puesto. */}
            {datos.anclaIgnora?.length > 0 && (
              <p className="ancla-aviso texto-mini">
                {t(
                  "outfits.anclaNoCoincide",
                  "No coincide con {motivos}. Se muestra igual porque la elegiste tú.",
                  {
                    motivos: datos.anclaIgnora
                      .map((m) => t(`outfits.motivoAncla.${m}`, MOTIVO_ANCLA[m] || m))
                      .join(t("outfits.niCon", " ni con ")),
                  }
                )}
              </p>
            )}
            <button className="boton-texto" onClick={quitarAncla}>
              {t("outfits.verTodasCombinaciones", "Ver todas las combinaciones")}
            </button>
          </div>
        </div>
      )}

      {/* Antes esto eran dos filas de chips: once botones que comían
          media pantalla del celular para algo que casi siempre queda
          en "todo". Dos selects ocupan una línea y dicen lo mismo.
          Los chips siguen siendo mejores cuando hay que VER los
          conteos —como en el armario— pero acá no hay conteos. */}
      {/* La estación va primera y como botones, no como select: es lo
          que más cambia el resultado y lo que más veces se toca. Un
          desplegable esconde el estado actual detrás de un clic.

          Volver a tocar la elegida la apaga y se ve el armario entero. */}
      <div className="termometro">
        {estacionesOrdenadas.map((e) => (
          <button
            key={e}
            type="button"
            className={`termo-op ${estacion === e ? "termo-activa" : ""}`}
            onClick={() => elegirEstacion(e)}
            title={
              e === estacionActual
                ? t("outfits.estacionHoy", "Es la estación de hoy donde estás")
                : t("outfits.vestirsePara", "Vestirse para {estacion}", {
                    /* El dato de una frase traducida también se
                       traduce: si no, el título dice "Dressing for
                       verano". */
                    estacion: tCat("estacion", e).toLowerCase(),
                  })
            }
          >
            {tCat("estacion", e)}
            {e === estacionActual && <span className="termo-hoy"> ·</span>}
          </button>
        ))}
      </div>

      {!estacion && (
        <p className="tenue texto-mini" style={{ margin: "0 0 8px" }}>
          {t("outfits.sinEstacion", "Sin estación elegida: se muestran combinaciones de todo el armario.")}
          {!estacionActual &&
            t("outfits.cargaPais", " Carga tu país en el perfil para que se elija sola.")}
        </p>
      )}

      <div className="barra-compacta">
        <Selector
          etiqueta={t("outfits.ocasion", "Ocasión")}
          grupo="ocasion"
          valores={opciones.categorias}
          valor={filtros.ocasion}
          onElegir={(v) => poner("ocasion", v)}
        />

        {/* "SIN MOSTRAR": armar solo con ropa que no fichaste.
            Es donde la marca vale de verdad — estás eligiendo qué
            ponerte, y querés algo que no se haya visto.

            No se esconde cuando no hay ninguna fichada: apagarlo no
            cambia nada y esconderlo obligaría a pedir el conteo de
            fichadas en una pantalla que hoy no lo pide. Un botón que no
            hace nada es más barato que un viaje más. */}
        <button
          type="button"
          className={`chip ${filtros.sinFichar ? "chip-activo" : ""}`}
          onClick={() => poner("sinFichar", filtros.sinFichar ? null : "1")}
          aria-pressed={!!filtros.sinFichar}
          title={t("outfits.sinMostrarTitulo", "Combina solo con ropa que todavía no mostraste en público")}
          style={{ display: "inline-flex", alignItems: "center", gap: "var(--e2)" }}
        >
          <IconoFichada relleno={false} tamano={14} />
          {t("outfits.sinMostrar", "Sin mostrar")}
        </button>
        {/* El select de Estación se fue: los botones de arriba hacen lo
            mismo y además deciden el abrigo. Dos controles preguntando
            lo mismo es una forma segura de que digan cosas distintas. */}

        {/* BUSCAR UNA PRENDA PARA ANCLAR — "quiero ponerme esto, ¿con
            qué va?".

            Vive acá, en la misma fila que la ocasión y "Otras
            opciones", y no en un panel plegable aparte: era una fila
            entera de pantalla para un link que casi siempre estaba
            cerrado. Los tres controles que modifican el resultado
            quedan juntos.

            Sin plegar: un input vacío ocupa lo mismo que el link que lo
            escondía, así que esconderlo no ahorraba nada. */}
        <div className="busca-ancla">
          <input
            className="busca-ancla-campo"
            type="text"
            value={textoAncla}
            onChange={(e) => setTextoAncla(e.target.value)}
            onFocus={cargarArmario}
            placeholder={t("outfits.combinarPrenda", "Combinar una prenda...")}
            aria-label={t("outfits.buscarPrendaCombinar", "Buscar una prenda para combinar")}
          />
          {textoAncla && (
            <button
              className="busca-ancla-limpiar"
              onClick={() => setTextoAncla("")}
              aria-label={t("outfits.borrarBusqueda", "Borrar la búsqueda")}
              type="button"
            >
              ×
            </button>
          )}

          {/* Los resultados flotan por encima en vez de empujar la
              pantalla: si empujaran, las combinaciones bailarían con
              cada letra que escribís. */}
          {textoAncla.trim() && (
            <div className="busca-ancla-lista">
              {!armario && (
                <p className="busca-ancla-nota">{t("outfits.cargandoArmario", "Cargando el armario...")}</p>
              )}
              {armario && candidatasAncla.length === 0 && (
                <p className="busca-ancla-nota">
                  {t("outfits.ningunaCoincide", 'Ninguna de tus {n} prendas coincide con "{texto}".', {
                    n: armario.length,
                    texto: textoAncla,
                  })}
                </p>
              )}
              {candidatasAncla.map((p) => (
                <button
                  key={p.id}
                  className="busca-ancla-item"
                  onClick={() => elegirAncla(p.id)}
                  type="button"
                >
                  {fotoChica(p) ? (
                    <img loading="lazy" decoding="async" src={fotoChica(p)} alt="" />
                  ) : (
                    <span className="busca-ancla-sinfoto" />
                  )}
                  <span className="busca-ancla-nombre">{p.nombre}</span>
                  <span className="tenue texto-mini">{tCat("tipo", p.tipo)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* "OTRAS OPCIONES" Y EL CONTADOR SE FUERON ABAJO (2026-08-21).
            Pedido del dueño, y es la misma decisión que bajó la barra de
            reglas: arriba va lo que decide QUÉ VER, abajo lo que se hace
            DESPUÉS de haber visto.

            "Otras opciones" no se toca hasta terminar de mirar las 18 —
            tenerlo arriba obligaba a subir para pedir más. Y el contador
            solo se entiende con las tarjetas ya vistas.

            Arriba queda únicamente "Buscando...", que sí califica lo que
            estás por ver. */}
        {cargando && <span className="conteo-outfits tenue">{t("outfits.buscando", "Buscando...")}</span>}

        {/* ⚠️  ACÁ HABÍA UN BOTÓN "LIMPIAR". SE SACÓ EL 2026-08-26,
            POR PEDIDO DE LUIS Y CON RAZÓN.

            Los dos filtros de esta pantalla ya se apagan solos: la
            ocasión tiene la opción "Todo" en su desplegable y "Sin
            mostrar" es un chip que alterna. Un botón para deshacer lo
            que el propio control deshace es un camino de más.

            Y hacía DOS cosas que no anunciaba:

              · Volvía a activar las reglas de estilo. Si habías tocado
                "Ver todas igual", "Limpiar" te las prendía de nuevo sin
                decirlo — e incoherente con la línea de al lado, que
                excluye `sin_reglas` del cálculo de `hayFiltros` a
                propósito. Si no cuenta para MOSTRAR el botón, no
                debería contar para borrarlo.

              · Dejaba la ocasión recordada en localStorage. Limpiaba la
                pantalla y al volver a abrir la app la ocasión estaba de
                nuevo. El desplegable, en cambio, pasa por `poner()`,
                que sí borra el recuerdo — o sea que el control limpia
                MEJOR que el botón de limpiar.

            `hayFiltros` se conserva: lo usa `SinCombinaciones` para
            decidir si el consejo es "sacá un filtro" o "cargá ropa". */}
      </div>

      {error && <div className="mensaje-error" role="alert">{error}</div>}

      {datos?.sinAbrigo > 0 && (
        <div className="aviso">
          {datos.sinAbrigo === datos.mostradas
            ? t("outfits.sinAbrigoTodas", "No tienes ningún abrigo que combine con estas prendas y sirva para este frío.")
            : t("outfits.sinAbrigoAlgunas", "{n} de estas combinaciones se quedaron sin abrigo.", { n: datos.sinAbrigo })}{" "}
          {t("outfits.estanMarcadas", "Están marcadas.")}
        </div>
      )}

      {datos?.conGastadas && (
        <div className="aviso">
          {t("outfits.conGastadas", "Para armar algo con esta ocasión hubo que incluir prendas gastadas. Están marcadas.")}
        </div>
      )}

      {/* Con Fresco o Frío la ropa de verano queda afuera. Si al sacarla
          no quedaba con qué cubrir el cuerpo, vuelve — pero se dice.
          Callarlo sería mostrar un short con frío sin explicación, que
          es exactamente lo que se vino a arreglar. */}
      {datos?.conFueraDeEstacion && (
        <div className="aviso">
          {t("outfits.conFueraDeEstacion", "No alcanzaba la ropa de temporada para este clima, así que hay prendas de primavera-verano entre las propuestas.")}
        </div>
      )}

      {!cargando && datos && datos.combinaciones.length === 0 && (
        <SinCombinaciones datos={datos} hayFiltros={hayFiltros} />
      )}

      {datos && datos.combinaciones.length > 0 && (
        <div className="grilla-outfits">
          {datos.combinaciones.map((c) => (
            <TarjetaOutfit
              key={c.id}
              combo={c}
              prendas={prendasDe(c)}
              editado={!!cambios[c.id]}
              onDeshacer={() => deshacerCambios(c.id)}
              onCambiarPrenda={(p) => abrirCambio(c, p)}
              onQuitarPrenda={(p) => quitarPrenda(c, p)}
              estado={registrados[claveDePrendas(prendasDe(c))]}
              onRegistrar={(fecha) => registrar(c, fecha)}
              perfumes={perfumes}
              perfumeHoy={perfumeHoy}
              onCambiarPerfume={setPerfumeHoy}
              onVetar={(a, b, motivo) => vetar(c, a, b, motivo)}
              motivos={opciones.motivosVeto || []}
              onAmpliar={(p) =>
                fotoGrande(p) && setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p })
              }
            />
          ))}
        </div>
      )}

      {/* "OTRAS OPCIONES" Y EL CONTADOR, DEBAJO DE LAS TARJETAS.
          Pedido del dueño (2026-08-21).

          Acá es donde se necesitan: nadie pide más opciones antes de
          mirar las que tiene. Arriba obligaba a volver a subir.

          EL CONTADOR DICE TRES COSAS, y la del medio no existía:

            18 en pantalla · ya viste 54 · de 4.956

          "Ya viste 54" es lo que faltaba para que "Otras opciones"
          signifique algo. Sin ese número no hay forma de saber si
          estás recorriendo el armario o dando vueltas sobre lo mismo:
          las combinaciones cambian, pero cambian tanto que parecen
          siempre nuevas.

          Se cuentan FIRMAS y no tarjetas. Si "Otras opciones" te
          devuelve algo que ya viste, no suma — el número promete "ya
          viste 54 distintas" y tiene que ser cierto. */}
      {datos && datos.combinaciones.length > 0 && (
        <div className="mas-opciones">
          <button
            className="boton-secundario"
            onClick={() => setSemilla((n) => n + 1)}
            disabled={cargando}
          >
            {cargando ? t("outfits.buscando", "Buscando...") : t("outfits.otrasOpciones", "Otras opciones")}
          </button>

          <p className="texto-mini tenue mas-opciones-conteo">
            <strong>{numero(datos.mostradas)}</strong> {t("outfits.enPantalla", "en pantalla")}
            {vistas.size > datos.mostradas && (
              <> · {t("outfits.yaViste", "ya viste")} <strong>{numero(vistas.size)}</strong> {t("outfits.distintas", "distintas")}</>
            )}
            {datos.total > datos.mostradas && (
              <>
                {t("outfits.deTotal", " · de ")}
                {datos.totalAproximado ? "~" : ""}
                {numero(datos.total)}
              </>
            )}
          </p>

          {/* Cuando ya recorriste todo, se dice. Seguir tocando el botón
              esperando algo nuevo es el peor uso posible de la pantalla,
              y sin este aviso no hay forma de darse cuenta. */}
          {datos.total > 0 &&
            !datos.totalAproximado &&
            vistas.size >= datos.total && (
              <p className="texto-mini tenue" style={{ margin: 0 }}>
                {t("outfits.yaVisteTodas", "Ya viste todas las combinaciones con estos filtros. Cambia un filtro para ver otras.")}
              </p>
            )}
        </div>
      )}

      {/* LA BARRA DE REGLAS BAJÓ AL FINAL (2026-08-20)
          Decisión del dueño: "es prioridad dar visibilidad a las
          combinaciones que opciones de navegación".

          Estaba arriba, antes de la primera tarjeta, y ocupaba una fila
          entera de la primera pantalla para decir algo que no cambia
          nunca —"Descartadas: 128 por color"— y ofrecer una salida
          —"Ver todas igual"— que se usa una vez cada tanto. Todos los
          días empujaba las sugerencias hacia abajo para hablar de las
          que NO están.

          Sigue existiendo por la misma razón de siempre: una regla que
          descarta en silencio y se equivoca es peor que no tener regla,
          porque te esconde el outfit que querías y nunca te enterás.
          Pero eso es una explicación, no una decisión de todos los
          días, y una explicación va donde uno la busca: al final, con
          las combinaciones ya vistas.

          En cambio los avisos de clima y de prendas gastadas SIGUEN
          ARRIBA: esos sí califican lo que estás por ver, y leerlos
          después de haber elegido llega tarde.

          Va SIN condición de que haya combinaciones. Si las reglas
          descartaron todo, `SinCombinaciones` te dice "probá Ver todas
          igual" — y ese botón vive acá. Escondiéndolo justo cuando no
          hay resultados, el consejo apuntaría a un botón inexistente,
          que es la peor versión de los dos mundos. */}
      {datos && <BarraReglas datos={datos} filtros={filtros} setFiltros={setFiltros} />}

      {datos?.complementos?.length > 0 && datos.combinaciones.length > 0 && (
        <div className="complementos">
          <p className="filtro-titulo">
            {t("outfits.paraSumar", "Para sumar encima (no entran en el cruce)")}
          </p>
          <div className="etiquetas">
            {datos.complementos.map((c) => (
              <span key={c.id} className="etiqueta">
                {c.nombre}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Selector de reemplazo. Modal y no desplegable dentro de la
          tarjeta: las tarjetas son angostas y el reemplazo se elige
          mirando fotos, que necesitan lugar. */}
      {cambiando && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setCambiando(null)}
        >
          <div className="modal-caja" ref={cajaCambiar} onClick={(e) => e.stopPropagation()}>
            <div className="modal-cabecera">
              {fotoChica(cambiando.prenda) && (
                <img loading="lazy" decoding="async" src={fotoChica(cambiando.prenda)} alt="" className="modal-foto" />
              )}
              <h2 className="modal-titulo">
                {t("outfits.cambiar", "Cambiar {nombre}", { nombre: cambiando.prenda.nombre })}
              </h2>
              <button
                className="modal-cerrar"
                onClick={() => setCambiando(null)}
                aria-label={t("comun.cerrar", "Cerrar")}
              >
                ×
              </button>
            </div>

            <div className="modal-cuerpo">
              <Alternativas
                prenda={cambiando.prenda}
                armario={armario}
                enUso={prendasDe(cambiando.combo).map((p) => p.id)}
                onElegir={(nueva) =>
                  cambiarPrenda(cambiando.combo, cambiando.prenda, nueva)
                }
              />
            </div>
          </div>
        </div>
      )}

      <Visor
        url={ampliada?.url}
        alt={ampliada?.alt}
        prenda={ampliada?.prenda}
        /* Cambiarle la estación a una prenda cambia qué combinaciones
           existen. Sin esto seguirías viendo las viejas.

           Y `olvidarArmario` va JUNTO, no en vez de: la prenda editada
           también está en la lista de reemplazos y en la de anclar. Ver
           el bloque de BA-13 arriba. */
        onPrendaEditada={() => {
          olvidarArmario();
          traer();
        }}
        onCerrar={() => setAmpliada(null)}
      />
    </section>
  );
}

/**
 * Las prendas que pueden ocupar el lugar de otra.
 *
 * Solo se ofrecen las que cubren exactamente las mismas zonas. Cambiar
 * una camisa por un enterizo dejaría el outfit cubriendo piernas dos
 * veces, y nadie pidió eso al tocar "cambiar".
 */
function Alternativas({ prenda, armario, enUso, onElegir }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  // El buscador va acá arriba y no adentro de un `if`: los hooks no
  // pueden vivir después de un `return` condicional.
  const [busqueda, setBusqueda] = useState("");

  // Se busca DENTRO de las alternativas válidas, no dentro del armario.
  // Es la diferencia entre acotar y romper: si buscara en el armario
  // entero, escribir "vestido" te ofrecería cambiar una camisa por un
  // enterizo y el outfit quedaría cubriendo piernas dos veces.
  // `enUso` es un array nuevo en cada render del padre. Convertirlo a
  // texto lo hace comparable: sin esto, `useMemo` recalcularía las 60
  // alternativas en cada tecla que se escribe en el buscador.
  const clave = (enUso || []).join(",");

  // Ojo: la clave es SOLO para comparar. Adentro se usa `enUso` tal
  // cual, porque `split(",")` devolvería strings y los ids de las
  // prendas son números — un `Set` de strings no reconocería ninguno y
  // el selector ofrecería prendas que ya están puestas.
  const opciones = useMemo(
    () => alternativasPara(prenda, armario, enUso),
    // eslint-disable-next-line
    [prenda, armario, clave]
  );

  const visibles = useMemo(
    () => filtrarPrendas(opciones, { texto: busqueda }),
    [opciones, busqueda]
  );

  if (!armario) return <p className="tenue">{t("outfits.cargandoTuArmario", "Cargando tu armario...")}</p>;

  if (opciones.length === 0) {
    return (
      <p className="tenue">
        {t("outfits.sinAlternativas", 'No tienes otra prenda que ocupe ese lugar. Es lo mismo que dice "qué comprar": esa parte del armario depende de una sola prenda.')}
      </p>
    );
  }

  // Con pocas opciones el buscador es ruido: ocupa una fila entera para
  // filtrar una lista que ya entra en la pantalla. El umbral es el mismo
  // criterio que en el resto de la app — buscar aparece cuando mirar
  // deja de alcanzar.
  const MINIMO_PARA_BUSCAR = 8;
  const hayBuscador = opciones.length >= MINIMO_PARA_BUSCAR;

  return (
    <>
      {hayBuscador && (
        <div className="buscador buscador-alternativas">
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={t("outfits.buscarAlternativas", "Buscar entre estas opciones...")}
            aria-label={t("outfits.buscarReemplazo", "Buscar prenda de reemplazo")}
            autoComplete="off"
          />
          {busqueda && (
            <button
              type="button"
              className="buscador-limpiar"
              onClick={() => setBusqueda("")}
              aria-label={t("outfits.limpiarBusqueda", "Limpiar búsqueda")}
            >
              ×
            </button>
          )}
        </div>
      )}

      <p className="tenue texto-mini">
        {busqueda
          ? t("outfits.xDeY", "{visibles} de {total}", {
              visibles: visibles.length,
              total: opciones.length,
            })
          : t("outfits.alternativasConteo", "{opciones} · primero las que hace más tiempo no usas", {
              opciones: tPlural("plural.opciones", opciones.length),
            })}
      </p>

      {/* Buscar y no encontrar nada tiene que decir POR QUÉ. Acá la
          respuesta casi nunca es "no la tenés": es "la tenés pero no
          puede ir en ese lugar". */}
      {visibles.length === 0 && (
        <p className="tenue">
          {t(
            "outfits.sinCoincidenciasLugar",
            'Ninguna de las opciones para ese lugar coincide con "{busqueda}". Puede que la prenda exista en tu armario pero no cubra la misma parte del cuerpo.',
            { busqueda }
          )}
        </p>
      )}

      <div className="armador-grilla">
        {visibles.map((p) => (
          <div key={p.id} className="armador-item">
            <button
              type="button"
              className="armador-boton"
              onClick={() => onElegir(p)}
            >
              {fotoChica(p) ? (
                <img loading="lazy" decoding="async" src={fotoChica(p)} alt="" className="prenda-foto" />
              ) : (
                <div className="prenda-foto prenda-sin-foto">
                  <span className="tenue texto-mini">{t("outfits.sinFoto", "Sin foto")}</span>
                </div>
              )}
              <span className="texto-mini armador-nombre">{p.nombre}</span>
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------
   Cuando no hay nada que mostrar

   El caso importante: "no hay combinaciones" casi nunca significa
   "tu armario está vacío". Significa que falta cubrir una zona del
   cuerpo con los filtros puestos. Decir cuál es la diferencia entre
   un error y un consejo.
------------------------------------------------------------------ */

function SinCombinaciones({ datos, hayFiltros }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  // Caso nuevo desde que hay reglas: el cuerpo se puede cubrir, pero
  // ninguna combinación pasó el filtro de estilo. Decirlo importa —
  // si no, el usuario va a buscar el problema en su armario.
  if (datos.vacioPorReglas) {
    return (
      <div className="aviso">
        <p>{t("outfits.vacioPorReglas", "El armario alcanza, pero las reglas de estilo descartaron todo.")}</p>
        <p className="tenue texto-chico">
          {t("outfits.vacioPorReglasAyuda", 'Suele pasar con una ocasión muy específica. Prueba "Ver todas igual" acá arriba para ver el crudo.')}
        </p>
      </div>
    );
  }

  /* ⚠️  EL ARMARIO VACÍO SE PREGUNTA PRIMERO (2026-08-24).
   *
   * Este bloque estaba DESPUÉS del de `faltantes`, y con el armario
   * vacío `faltantes` trae las tres zonas. O sea que el primer mensaje
   * que leía TODO usuario nuevo, en la pantalla principal, era:
   *
   *     "No hay con qué cubrir torso y piernas y pies"
   *
   * Jerga de motor de combinaciones, en el minuto uno, cuando la
   * respuesta obvia era "todavía no cargaste nada". El texto correcto
   * existía y estaba escrito tres líneas más abajo, inalcanzable.
   *
   * Es el mismo tipo de error que el de la bienvenida (AL-5): la app
   * sabía la respuesta y mostraba otra.
   */
  if (datos.prendasConsideradas === 0) {
    return (
      <div className="aviso">
        <p>{t("outfits.sinRopa", "Todavía no cargaste ropa.")}</p>
        <p className="tenue texto-chico">
          {t("outfits.sinRopaAyuda", "Sácale una foto a lo que tengas y la app la clasifica sola. Con una prenda de arriba, una de abajo y un calzado ya empieza a proponerte conjuntos.")}
        </p>
      </div>
    );
  }

  if (datos.faltantes?.length > 0) {
    // "torso y piernas" es como piensa el motor, no como piensa una
    // persona. Se traduce a ropa: nadie dice "me falta cubrir el torso".
    const ROPA_DE = {
      torso: t("outfits.zonaTorso", "algo para arriba"),
      piernas: t("outfits.zonaPiernas", "algo para abajo"),
      pies: t("outfits.zonaPies", "un calzado"),
    };
    const zonas = datos.faltantes
      .map((z) => ROPA_DE[z] || z)
      .join(t("outfits.conectorY", " y "));
    return (
      <div className="aviso">
        <p>
          {t("outfits.teFalta", "Te falta")} <strong>{zonas}</strong>
          {hayFiltros ? t("outfits.conEstosFiltros", " con estos filtros") : ""}.
        </p>
        <p className="tenue texto-chico">
          {hayFiltros
            ? t("outfits.faltaAyudaFiltros", "Prueba sacando algún filtro, o marca más prendas con esta ocasión desde el armario.")
            : t("outfits.faltaAyudaCarga", "Carga alguna prenda de eso y aparecen las combinaciones.")}
        </p>
      </div>
    );
  }

  return <p className="tenue">{t("outfits.ningunaConFiltros", "Ninguna combinación con estos filtros.")}</p>;
}

// La franja de colores de una combinación.
//
// Reusa `tintesDelOutfit` —el mismo cálculo que dibuja los días del
// calendario— para que un outfit se vea igual en las dos pantallas. Si
// cada una tuviera su propia lógica, el mismo conjunto podría mostrar
// tres franjas acá y cuatro allá, y nadie sabría cuál creer.
//
// Si ninguna prenda tiene colores cargados no dibuja nada, en vez de
// dejar un recuadro vacío que parece un error de carga.
function ColoresDelOutfit({ prendas }) {
  const tintes = tintesDelOutfit(prendas);
  if (tintes.length === 0) return null;

  return (
    <span
      className="outfit-colores"
      title={tintes.map((t) => t.nombre).join(" · ")}
    >
      {tintes.map((t, i) => (
        <span
          key={`${t.nombre}-${i}`}
          className={`outfit-color ${
            t.tinte === DESCONOCIDO ? "calendario-franja-desconocida" : ""
          }`}
          style={t.tinte === DESCONOCIDO ? undefined : { background: t.tinte }}
        />
      ))}
    </span>
  );
}

function BarraReglas({ datos, filtros, setFiltros }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  const d = datos.descartadas || {};
  const total = (d.color || 0) + (d.formalidad || 0) + (d.veto || 0);
  const apagadas = filtros.sin_reglas === "1";

  if (total === 0 && !apagadas && !datos.vetosActivos) return null;

  /* ⚠️  ACÁ NO VA UN NÚMERO, Y ES A PROPÓSITO (BA-5, 2026-09-06).
   *
   * `descartadas` cuenta PODAS del árbol de enumeración, no
   * combinaciones. Cuando el motor descarta una camisa contra un
   * pantalón, se poda una rama entera: los doce calzados que colgaban
   * de ahí nunca se enumeran, y suman UNO al contador. Medido, el
   * número que se mostraba llegaba a ser miles de veces más grande que
   * los outfits que realmente existían — "descarté 2.400.000 por
   * color" para un armario que da trece mil combinaciones.
   *
   * Contarlas de verdad exige multiplicar cada poda por el tamaño del
   * subárbol cortado, en la ruta más caliente que tiene la app (el
   * enumerador ya se lleva el 28% del pedido). Y no compra nada: lo
   * que el usuario necesita saber es QUÉ regla está filtrando, para
   * poder apagarla con el botón que está al lado. Cuántas, no.
   *
   * El contador sigue existiendo: decide SI la regla se nombra. Es
   * exactamente para lo que sirve un conteo de podas. */
  const partes = [];
  if (d.color) partes.push(t("outfits.porColor", "por color"));
  if (d.formalidad) partes.push(t("outfits.porFormalidad", "por formalidad"));
  if (d.veto) partes.push(t("outfits.porVetos", "por vetos tuyos"));

  return (
    <div className="barra-reglas texto-mini">
      <span className="tenue">
        {apagadas
          ? t("outfits.reglasApagadas", "Reglas de estilo apagadas: se muestra todo lo que cubre el cuerpo.")
          : partes.length > 0
          ? t("outfits.descartadas", "Se descartaron combinaciones {partes}.", {
              partes: partes.join(", "),
            })
          : t("outfits.reglasActivas", "Reglas de estilo activas.")}
        {datos.vetosLeidos === false &&
          t("outfits.vetosNoLeidos", " (no se pudieron leer tus vetos)")}
      </span>
      <button
        className="boton-texto"
        onClick={() =>
          setFiltros((a) => {
            const n = { ...a };
            if (apagadas) delete n.sin_reglas;
            else n.sin_reglas = "1";
            return n;
          })
        }
      >
        {apagadas ? t("outfits.activarReglas", "Activar reglas") : t("outfits.verTodasIgual", "Ver todas igual")}
      </button>
    </div>
  );
}

// Un select con la etiqueta adentro. Ocupa una línea y no dos, y en
// el celular abre el selector nativo, que es más cómodo que once
// botones chiquitos.
function Selector({ etiqueta, valores, valor, onElegir, grupo }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  if (!valores?.length) return null;

  return (
    <label className={`selector ${valor ? "selector-activo" : ""}`}>
      <span className="selector-etiqueta">{etiqueta}</span>
      <select value={valor || ""} onChange={(e) => onElegir(e.target.value)}>
        <option value="">{t("outfits.todo", "Todo")}</option>
        {valores.map((v) => (
          <option key={v} value={v}>
            {/* ⚠️  TERCERA VEZ LA MISMA CLASE, Y EL DETECTOR NO PUEDE
                VERLA. `valores` es una PROP: desde acá no hay forma
                estática de saber que adentro viene un valor de
                catálogo, y por eso `test_valores_crudos.js` declara
                este punto ciego en su cabecera.

                El valor que viaja al servidor sigue siendo el español
                guardado (`value={v}`); lo único que se traduce es lo
                que se lee. */}
            {grupo ? tCat(grupo, v) : v}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------------------
   Una combinación
------------------------------------------------------------------ */

function TarjetaOutfit({
  combo,
  prendas: prendasEfectivas,
  editado,
  onDeshacer,
  onCambiarPrenda,
  onQuitarPrenda,
  estado,
  onRegistrar,
  perfumes,
  perfumeHoy,
  onCambiarPerfume,
  onVetar,
  onAmpliar,
  motivos,
}) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  // Tres estados de pie de tarjeta: normal, eligiendo fecha, eligiendo
  // qué par no va. Uno por vez — abrir los dos juntos convierte una
  // tarjeta en un formulario.
  const [panel, setPanel] = useState(null);
  const [fecha, setFecha] = useState(hoyLocal());

  // MODO EDITAR
  //
  // Cambiar una prenda ya se podía: había un ⇄ de 26px en la esquina de
  // cada foto. Nadie lo encontraba, y encima estaba SIEMPRE ahí,
  // sumando tres botones a cada tarjeta en la vista de todos los días.
  //
  // Ahora hay un botón "Editar" en el pie, junto a los otros dos. Al
  // encenderlo aparecen los ⇄ y se puede cambiar una, dos o las tres
  // prendas sin salir: cada cambio se acumula y el modo sigue abierto
  // hasta que tocás "Listo".
  //
  // Que se apague por defecto limpia la vista normal, que es la que se
  // mira todos los días.
  const [editando, setEditando] = useState(false);

  // El par elegido, mientras se espera el motivo. Vetar son dos
  // preguntas y no una: CUÁLES no van (sin eso el veto no sirve de
  // nada, porque guardar el outfit entero no generaliza) y POR QUÉ
  // (que es lo que me dice si la que falló fue una regla mía).
  const [parElegido, setParElegido] = useState(null);

  // Torso arriba, piernas al medio, pies abajo. Verlo en el orden en que
  // se usa el cuerpo hace que se lea de un vistazo; en orden alfabético
  // hay que reconstruirlo mentalmente cada vez.
  const orden = { torso: 0, piernas: 1, pies: 2 };
  const prendas = [...prendasEfectivas].sort((a, b) => {
    // El abrigo va último aunque cubra torso: se pone ENCIMA de todo,
    // y verlo primero hace pensar que reemplaza a la camisa.
    const za = a.tipo === "Abrigo" ? 3 : orden[(a.cubre || [])[0]] ?? 9;
    const zb = b.tipo === "Abrigo" ? 3 : orden[(b.cubre || [])[0]] ?? 9;
    return za - zb;
  });

  return (
    <article className="outfit">
      <div className={`outfit-fotos ${editando ? "outfit-fotos-editando" : ""}`}>
        {prendas.map((p) => (
          <div key={p.id} className="outfit-prenda">
            {fotoChica(p) ? (
              <img loading="lazy" decoding="async"
                src={fotoChica(p)}
                alt={p.nombre}
                className="prenda-foto"
                // En modo editar la foto entera es el blanco: apuntarle
                // a un círculo de 26px con el pulgar es peor que
                // apuntarle a la prenda.
                onClick={() => (editando ? onCambiarPrenda(p) : onAmpliar(p))}
                title={editando ? t("outfits.cambiar", "Cambiar {nombre}", { nombre: p.nombre }) : t("outfits.ampliar", "Ampliar")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  /* Enter y espacio hacen lo mismo que el click, sea lo que
                     sea. No se duplica la expresión del onClick: se dispara
                     el click de verdad. Copiar el handler exigía parsear JSX
                     con llaves anidadas, que es justamente lo que este
                     proyecto ya aprendió a no hacer. */
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}
              />
            ) : (
              <div className="prenda-foto prenda-sin-foto">
                <span className="tenue texto-mini">{t("outfits.sinFoto", "Sin foto")}</span>
              </div>
            )}

            {/* Cambiar esta prenda sin perder el resto del outfit. Es el
                caso frecuente: te gusta la combinación pero no ese
                calzado. Antes la única salida era barajar todo y perder
                lo que sí te servía.

                Solo aparece en modo editar: tres botones flotando sobre
                cada tarjeta, todo el tiempo, es ruido en la pantalla que
                se mira todos los días. */}
            {editando && (
              <button
                type="button"
                className="outfit-cambiar"
                onClick={() => onCambiarPrenda(p)}
                title={t("outfits.cambiar", "Cambiar {nombre}", { nombre: p.nombre })}
              >
                ⇄
              </button>
            )}

            {/* Sacar esta prenda. Se apaga cuando queda una sola: un
                outfit vacío no es un outfit, y el servidor lo rechaza.
                Apagar el botón dice eso antes de que pase, en vez de
                dejar tocar y explicar el error después. */}
            {editando && (
              <button
                type="button"
                className="outfit-quitar"
                onClick={() => onQuitarPrenda(p)}
                disabled={prendas.length <= 1}
                title={
                  prendas.length <= 1
                    ? t("outfits.noSacarUltima", "Tiene que quedar al menos una prenda")
                    : t("outfits.quitar", "Sacar {nombre}", { nombre: p.nombre })
                }
                aria-label={t("outfits.quitar", "Sacar {nombre}", { nombre: p.nombre })}
              >
                ×
              </button>
            )}

            <p className="outfit-nombre texto-mini">
              {p.id === combo.abrigo?.id && <span className="marca-abrigo">+ </span>}
              {p.nombre}
              {p.estado === "Gastada" && (
                <span className="etiqueta-gastada">{t("outfits.gastada", " gastada")}</span>
              )}
            </p>
          </div>
        ))}
      </div>

      {combo.avisoClima && (
        <p className="texto-mini aviso-clima">
          {combo.avisoClima === "sin_abrigo_disponible"
            ? t("outfits.sinAbrigoPegue", "Sin abrigo que pegue con esto")
            : t("outfits.cortoDeAbrigo", "Puede quedarte corto de abrigo")}
        </p>
      )}

      {/* Acá se mostraba la estación de cada combinación. Se sacó: es
          la misma para las 18 tarjetas, así que repetirla no informa.

          Al sacarla quedó media fila en blanco, porque el pie era un
          `space-between` con nada del lado izquierdo. En vez de rellenar
          el hueco con algo, se rediseñó la fila: "No combina" ocupa lo justo
          y "Me lo puse" se estira con el resto.

          Gana dos cosas: no queda espacio muerto, y la acción principal
          —la que se toca todos los días— pasa a ser un blanco grande en
          vez de un botón chico en una esquina. */}
      {editado && (
        <button
          className="boton-texto outfit-editado outfit-editado-linea"
          onClick={onDeshacer}
        >
          {t("outfits.editadoDeshacer", "Editado · deshacer")}
        </button>
      )}

      {editando && (
        <p className="outfit-ayuda texto-mini">
          {t("outfits.ayudaEditar", "Toca la prenda que quieras cambiar. Puedes cambiar una, dos o las tres.")}
        </p>
      )}

      <div className="outfit-pie">
        {/* La franja de colores del outfit, a la izquierda de "No combina".
            Es el mismo dibujo que usa cada día del calendario, y por eso
            vale acá: con 18 tarjetas en pantalla, la franja deja
            comparar de un vistazo cuáles son variaciones del mismo
            esquema de color y cuáles son propuestas distintas de verdad.
            Las fotos no permiten eso — obligan a mirar prenda por
            prenda.

            Va horizontal y no vertical como en el calendario: acá el
            hueco disponible es una tira ancha y baja, no un cuadrado.

            Ocupa el espacio que sobra a la izquierda. Antes ahí no había
            nada. */}
        <ColoresDelOutfit prendas={prendas} />

        {/* ⚠️  EL VETO NECESITA DOS PRENDAS, Y DESDE QUE SE PUEDEN SACAR
            PUEDE QUEDAR UNA.

            Vetar no guarda el outfit: guarda el PAR que molesta (por eso
            el panel pregunta cuál). Con una sola prenda no hay ningún
            par posible, así que el panel se abría con la pregunta y
            CERO opciones debajo: un callejón sin salida, sin error.

            Es un agujero que abrió la función de sacar prendas
            (2026-08-26) y que no existía antes, porque el motor nunca
            devuelve combinaciones de una sola prenda. */}
        {!estado && prendas.length >= 2 && (
          <button
            type="button"
            className="boton-veto"
            onClick={() => setPanel(panel === "veto" ? null : "veto")}
            title={t("outfits.noMeGusta", "Esta combinación no me gusta")}
          >
            {t("outfits.noVa", "No combina")}
          </button>
        )}

        {!estado && (
          <button
            type="button"
            className={`boton-editar ${editando ? "boton-editar-activo" : ""}`}
            onClick={() => {
              setEditando((v) => !v);
              // Abrir el modo con un panel abierto deja dos cosas
              // pidiendo atención en la misma tarjeta.
              setPanel(null);
            }}
            title={t("outfits.tituloEditar", "Reemplazar prendas de esta combinación")}
          >
            {editando ? t("outfits.listo", "Listo") : t("outfits.editar", "Editar")}
          </button>
        )}

        <button
          type="button"
          className={`outfit-puse ${
            estado === "ok" ? "boton-texto" : "boton-secundario"
          }`}
          onClick={() => setPanel(panel === "fecha" ? null : "fecha")}
          disabled={!!estado}
        >
          {estado === "ok"
            ? t("outfits.registrado", "✓ Registrado")
            : estado === "yendo"
            ? "..."
            : t("outfits.meLoPuse", "Me lo puse")}
        </button>
      </div>

      {panel === "fecha" && (
        <div className="outfit-panel">
          {/* El frasco va PRIMERO: se elige el perfume y después el día,
              que es el toque que registra. Al revés, tocar «Hoy»
              registraría antes de poder elegirlo. */}
          <FrascoBoton perfumes={perfumes} valor={perfumeHoy} onCambiar={onCambiarPerfume} />
          {/* Hoy es el 99% de los casos y está a un clic. La fecha
              exacta existe para el 1% —registrar el domingo lo del
              viernes— sin cobrarle ese trabajo a los otros. */}
          <div className="opciones-chips">
            <button className="chip" onClick={() => onRegistrar(hoyLocal())}>
              {t("outfits.hoy", "Hoy")}
            </button>
            <button className="chip" onClick={() => onRegistrar(hace(1))}>
              {t("outfits.ayer", "Ayer")}
            </button>
            <button className="chip" onClick={() => onRegistrar(hace(2))}>
              {t("outfits.anteayer", "Anteayer")}
            </button>
          </div>
          <div className="fila-fecha">
            <input
              type="date"
              value={fecha}
              max={hoyLocal()}
              onChange={(e) => setFecha(e.target.value)}
            />
            <button className="boton-secundario" onClick={() => onRegistrar(fecha)}>
              {t("outfits.registrar", "Registrar")}
            </button>
          </div>
        </div>
      )}

      {panel === "veto" && !parElegido && (
        <div className="outfit-panel">
          {/* Acá está lo que hace que vetar sirva: no se guarda el
              outfit, se guarda el PAR que molesta. Por eso hay que
              preguntar cuál es. Un clic más, a cambio de que el veto
              se lleve puestas cientos de combinaciones y no una. */}
          <p className="texto-mini tenue" style={{ margin: "0 0 6px" }}>
            {t("outfits.quePar", "¿Qué dos prendas no van juntas?")}
          </p>
          <div className="opciones-chips">
            {pares(prendas).map(([a, b]) => (
              <button
                key={`${a.id}-${b.id}`}
                className="chip"
                onClick={() => setParElegido([a, b])}
              >
                {corto(a.nombre)} + {corto(b.nombre)}
              </button>
            ))}
          </div>
        </div>
      )}

      {panel === "veto" && parElegido && (
        <div className="outfit-panel">
          <p className="texto-mini" style={{ margin: "0 0 6px" }}>
            <strong>{corto(parElegido[0].nombre)}</strong> +{" "}
            <strong>{corto(parElegido[1].nombre)}</strong> {t("outfits.porQueNoVa", "— ¿por qué no va?")}
          </p>
          <div className="opciones-chips">
            {motivos.map((m) => (
              <button
                key={m.valor}
                className="chip"
                onClick={() => onVetar(parElegido[0].id, parElegido[1].id, m.valor)}
              >
                {/* Etiqueta del servidor, traducida por clave. Ver
                    lib/opciones_api.js. */}
                {t(`opciones.motivoVeto.${m.valor}`, m.etiqueta)}
              </button>
            ))}
          </div>
          {/* Se puede saltear. El motivo me sirve a mí para calibrar
              las reglas; obligarlo convertiría un gesto de un segundo
              en un formulario, y el veto dejaría de usarse. */}
          <button
            className="boton-texto"
            style={{ width: "auto", margin: "6px 0 0", padding: 0 }}
            onClick={() => onVetar(parElegido[0].id, parElegido[1].id, null)}
          >
            {t("outfits.prefieroNoDecir", "Prefiero no decir")}
          </button>
        </div>
      )}
    </article>
  );
}

// Todos los pares de un conjunto. Con 3 prendas son 3 pares; con un
// enterizo + calzado, 1.
function pares(prendas) {
  const salida = [];
  for (let i = 0; i < prendas.length; i++) {
    for (let j = i + 1; j < prendas.length; j++) {
      salida.push([prendas[i], prendas[j]]);
    }
  }
  return salida;
}

// Los nombres que vienen de Notion son largos a propósito. Dentro de un
// chip hay que recortarlos o el panel se vuelve ilegible.
function corto(nombre, max = 22) {
  return nombre.length > max ? `${nombre.slice(0, max - 1)}…` : nombre;
}
